/**
 * SSN formatting and masking utilities for the patient profile (Create/Edit/View).
 *
 * Mirrors the frontend-portal therapist SSN behavior:
 * - View / pre-touch edit: only last 4 digits visible (XXX-XX-1234).
 * - While typing: progressive mask, only the last-4 positions ever show real digits.
 * - The stored/validated value is always the cleaned, dashed real SSN (XXX-XX-XXXX).
 */

/** Strip everything except digits and cap to a 9-digit SSN. */
export const cleanSSNDigits = (value: string | null | undefined): string =>
  (value ?? '').replace(/\D/g, '').slice(0, 9);

/**
 * Mask SSN for read-only display: only last 4 digits visible (XXX-XX-1234).
 * Returns '' if fewer than 4 digits are present.
 */
export const maskSSNForDisplay = (value: string | null | undefined): string => {
  if (value == null || value === '') return '';
  const cleaned = value.replace(/\D/g, '');
  if (cleaned.length < 4) return '';
  const last4 = cleaned.slice(-4);
  return `XXX-XX-${last4}`;
};

/** Lookup for SSN mask display when 1–5 digits entered (avoids per-call string allocation). */
const SSN_INPUT_MASK: readonly [string, string, string, string, string] = [
  'X',
  'XX',
  'XXX',
  'XXX-X',
  'XXX-XX',
];

/**
 * Mask SSN during input: display builds with format; only last 4 digit positions show actual digits.
 * 1 → X,  12 → XX,  123 → XXX,  1234 → XXX-X,  12345 → XXX-XX,  123456 → XXX-XX-6,  …  123456789 → XXX-XX-6789
 */
export const maskSSNForInputDisplay = (value: string | null | undefined): string => {
  const cleaned = cleanSSNDigits(value);
  if (!cleaned.length) return '';
  if (cleaned.length <= 5) return SSN_INPUT_MASK[cleaned.length - 1];
  return `XXX-XX-${cleaned.slice(5)}`;
};

/** Format partial or full SSN with dashes (XXX-XX-XXXX). This is the value we store/validate. */
export const formatSSNPartial = (value: string | null | undefined): string => {
  const d = cleanSSNDigits(value);
  if (d.length <= 3) return d;
  if (d.length <= 5) return `${d.slice(0, 3)}-${d.slice(3)}`;
  return `${d.slice(0, 3)}-${d.slice(3, 5)}-${d.slice(5)}`;
};
